import hash from 'object-hash';
import { compose, toNumber, compact, split } from 'lodash/fp';
import { UnicodeEntity } from '@mews/enums';
import { TextKey } from '@mews-commander/enums';
import { swap } from '@mews-commander/utils';
import { parseNonNullValueToNumber } from '../utils';
import { EnterpriseBuilderImport, SimpleResource, SimpleResourceCategory } from '../types';

export const createResourceStub = ({
    name,
    floorId,
    categoryId,
    resourceCategoryIndexes,
    index = null,
    parentResourceBuilderId = null,
    parentResourceIndex = null,
}: SimpleResource) => {
    const resourceStub = {
        name,
        floorId,
        categoryId,
        parentResourceBuilderId,
        resourceCategoryIndexes,
        index: parseNonNullValueToNumber(index),
        parentResourceIndex: parseNonNullValueToNumber(parentResourceIndex),
    };

    return {
        ...resourceStub,
        id: hash(resourceStub),
    };
};

export const createCategoryFromValues = ({
    name,
    id,
    index,
    resourceCategoryType,
    capacity,
    shortcut,
    extraCapacity = null,
    description = null,
    ...other
}: SimpleResourceCategory) => ({
    id,
    name,
    resourceCategoryType,
    capacity: toNumber(capacity),
    extraCapacity: parseNonNullValueToNumber(extraCapacity),
    shortcut,
    description,
    index,
    ...other,
});

export const splitLines = compose(compact, split(UnicodeEntity.NewLine));

export const getNewOrder = (prevOrdering: Array<string>, offset: number, id: string) => {
    const index = prevOrdering.indexOf(id);
    const newIndex = index + offset;

    return swap(index, newIndex, prevOrdering);
};

export const getFileDataObject = async (file: File | undefined): Promise<EnterpriseBuilderImport> =>
    new Promise((resolve, reject) => {
        if (!file) {
            reject(new Error(TextKey.FileNotFound));
            return;
        }

        const reader = new window.FileReader();
        reader.readAsText(file);
        reader.onload = () => {
            try {
                // result is always a string after readAsText is called - https://developer.mozilla.org/en-US/docs/Web/API/FileReader/result#value
                if (typeof reader.result === 'string') {
                    resolve(JSON.parse(reader.result));
                }
            } catch (e) {
                reject(new Error(TextKey.ImportFailedErrorMessage));
            }
        };

        reader.onerror = () => {
            reject(new Error(TextKey.ImportFailedErrorMessage));
        };
    });
